import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../i18n";
import { LOGO, useMeta } from "../components/bits";
import { ACCOUNT_TYPES } from "../lib/options";

const Shell = ({ title, children }) => (
  <div className="min-h-[80vh] grid place-items-center px-4 py-12">
    <div className="w-full max-w-md card p-8 fade-up">
      <img src={LOGO} alt="PrideFocus.world" className="h-16 mx-auto" />
      <h1 className="text-2xl font-bold text-center mt-4 mb-6">{title}</h1>
      {children}
    </div>
  </div>
);

export function Login() {
  const { t } = useI18n();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useMeta(t("auth.login"));
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", f); setUser(data);
      nav(sp.get("next") || (data.profile?.onboarding_done ? "/dashboard" : "/onboarding"));
    } catch (er) { setErr(errMsg(er)); }
    setBusy(false);
  };
  return (
    <Shell title={t("auth.welcome")}>
      <form onSubmit={submit} className="space-y-4">
        <div><label className="lbl" htmlFor="email">{t("auth.email")}</label><input id="email" type="email" required className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></div>
        <div><label className="lbl" htmlFor="pw">{t("auth.password")}</label><input id="pw" type="password" required className="input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></div>
        {err && <p className="text-sm text-red-600" role="alert" data-testid="login-error">{err}</p>}
        <button className="btn-accent w-full" disabled={busy} data-testid="login-submit-btn">{t("auth.login")}</button>
        <div className="flex justify-between text-sm"><Link to="/forgot-password" className="text-orange-600" data-testid="forgot-link">{t("auth.forgot")}</Link><Link to="/register" className="font-semibold">{t("auth.no_account")}</Link></div>
      </form>
    </Shell>
  );
}

export function Register() {
  const { t, lang } = useI18n();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "", account_type: sp.get("type") || "individual", age_confirmed: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useMeta(t("auth.register"));
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (!f.age_confirmed) return setErr(t("auth.age"));
    setBusy(true);
    try { const { data } = await api.post("/auth/register", { ...f, ui_lang: lang }); setUser(data); nav(f.account_type === "employer" ? "/employer?tab=company" : "/onboarding"); }
    catch (er) { setErr(errMsg(er)); }
    setBusy(false);
  };
  return (
    <Shell title={t("nav.join")}>
      <form onSubmit={submit} className="space-y-4">
        <div><label className="lbl" htmlFor="remail">{t("auth.email")}</label><input id="remail" type="email" required className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" /></div>
        <div><label className="lbl" htmlFor="rpw">{t("auth.password")}</label><input id="rpw" type="password" required minLength={8} className="input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password-input" /><p className="text-xs muted mt-1">8+ characters</p></div>
        <fieldset><legend className="lbl">{t("auth.profile_type")}</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{ACCOUNT_TYPES.map((a) => (
            <button type="button" key={a} onClick={() => setF({ ...f, account_type: a })} className={`text-left text-sm rounded-xl border px-3 py-2.5 transition-colors ${f.account_type === a ? "border-orange-500 bg-orange-50 font-semibold" : "border-slate-200 hover:border-orange-300"}`} data-testid={`register-type-${a}`}>{t(`types.${a}`)}</button>))}
          </div></fieldset>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={f.age_confirmed} onChange={(e) => setF({ ...f, age_confirmed: e.target.checked })} className="mt-1 accent-orange-500" data-testid="register-age-checkbox" />{t("auth.age")}</label>
        <p className="text-xs muted">By creating an account you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>. No degree required.</p>
        {err && <p className="text-sm text-red-600" role="alert" data-testid="register-error">{err}</p>}
        <button className="btn-accent w-full" disabled={busy} data-testid="register-submit-btn">{t("auth.register")}</button>
        <p className="text-sm text-center">{t("auth.have_account")} <Link to="/login" className="font-semibold text-orange-600">{t("auth.login")}</Link></p>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (er) { toast.error(errMsg(er)); } };
  return (
    <Shell title={t("auth.forgot")}>
      {sent ? <p className="text-center text-slate-600" data-testid="forgot-sent">If an account exists for that email, a reset link is on its way. It expires in one hour.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <input type="email" required className="input" placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" aria-label={t("auth.email")} />
          <button className="btn-accent w-full" data-testid="forgot-submit-btn">{t("auth.send_link")}</button>
        </form>)}
    </Shell>
  );
}

export function Reset() {
  const { t } = useI18n();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Password updated. Please sign in."); nav("/login"); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Shell title={t("auth.reset")}>
      <form onSubmit={submit} className="space-y-4">
        <input type="password" minLength={8} required className="input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (8+ characters)" data-testid="reset-password-input" aria-label="New password" />
        <button className="btn-accent w-full" data-testid="reset-submit-btn">{t("save")}</button>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading");
  useEffect(() => {
    api.post("/auth/verify-email", { token: sp.get("token") || "" }).then(() => { setState("ok"); refresh(); }).catch((e) => setState(errMsg(e)));
  }, [sp, refresh]);
  return (
    <Shell title="Email verification">
      <p className="text-center" data-testid="verify-status">{state === "loading" ? "Verifying…" : state === "ok" ? "Your email is verified. Thank you!" : state}</p>
      <Link to="/dashboard" className="btn-primary w-full mt-6">Continue</Link>
    </Shell>
  );
}
